import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '@/app/dashboard/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EventTypeSection } from '@/components/ui/event-type-section';
import { EventTypeDetails } from '@/components/ui/event-type-details';
import { EventTypeDuration } from '@/components/ui/event-type-duration';
import { EventTypeHostsSelector } from '@/components/ui/event-type-hosts-selector';
import { EventTypeStatus } from '@/components/ui/event-type-status';
import { useToast } from '@/hooks/use-toast';
import {
  getEventTypeById,
  createEventType,
  updateEventType,
  getSchedules,
  getSchedule,
  updateSchedule,
  createSchedule,
} from '@/client/sdk.gen';
import {
  EventTypeCategory,
  EventTypeResponse,
  EventTypeCreate,
  EventTypeUpdate,
  Schedule,
  DayOfWeek,
  DayInterval,
  WeekOfDayConfig,
  OrganisationMemberResponse,
} from '@/client/types.gen';
import {
  ArrowLeft,
  Clock,
  User,
  Users,
  Info,
  Settings,
  Calendar,
  Link,
  Loader2,
  CalendarRange,
  Bell,
  Timer,
  Hourglass,
  AlertCircle,
  InfoIcon,
  Copy,
  Plus,
  CheckCircle,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScheduleEditor } from '@/components/ui/schedule-editor';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { Switch } from '@/components/ui/switch';

interface LocationState {
  duplicate?: boolean;
  eventTypeData?: EventTypeResponse;
}

const EVENT_TYPE_DESCRIPTIONS: Record<EventTypeCategory, string> = {
  ONE_ON_ONE: 'One-on-one appointments between you and one attendee',
  GROUP: 'Group appointments with you and multiple attendees',
  COLLECTIVE: 'Appointments where multiple hosts attend with the attendee(s)',
  ROUND_ROBIN:
    'Appointments assigned to one host from a group based on availability',
};

// Add default schedule and availability constants
const DEFAULT_SCHEDULE: Record<DayOfWeek, DayInterval[]> = {
  MONDAY: [{ start_time: '09:00', end_time: '17:00' }],
  TUESDAY: [{ start_time: '09:00', end_time: '17:00' }],
  WEDNESDAY: [{ start_time: '09:00', end_time: '17:00' }],
  THURSDAY: [{ start_time: '09:00', end_time: '17:00' }],
  FRIDAY: [{ start_time: '09:00', end_time: '17:00' }],
  SATURDAY: [],
  SUNDAY: [],
};

const DEFAULT_AVAILABILITY: Record<DayOfWeek, boolean> = {
  MONDAY: true,
  TUESDAY: true,
  WEDNESDAY: true,
  THURSDAY: true,
  FRIDAY: true,
  SATURDAY: false,
  SUNDAY: false,
};

// Add helper functions for schedule display
const formatDaysDisplay = (schedule: Schedule): string => {
  if (!schedule?.schedule_config) return 'No availability set';

  const configs = schedule.schedule_config.week_of_day_configs || [];
  const enabledDays = configs
    .filter((config) => config.enabled)
    .map((config) => config.day_of_week);

  if (enabledDays.length === 0) return 'No days available';
  if (enabledDays.length === 7) return 'Every day';

  // Check for weekdays only
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  const weekdaysOnly =
    weekdays.every((day) => enabledDays.includes(day as DayOfWeek)) &&
    !enabledDays.includes('SATURDAY' as DayOfWeek) &&
    !enabledDays.includes('SUNDAY' as DayOfWeek);

  if (weekdaysOnly) return 'Weekdays';

  // Abbreviate days
  const dayMap: Record<DayOfWeek, string> = {
    MONDAY: 'Mon',
    TUESDAY: 'Tue',
    WEDNESDAY: 'Wed',
    THURSDAY: 'Thu',
    FRIDAY: 'Fri',
    SATURDAY: 'Sat',
    SUNDAY: 'Sun',
  };

  return enabledDays.map((day) => dayMap[day]).join(', ');
};

const formatTimeDisplay = (schedule: Schedule): string => {
  if (!schedule?.schedule_config) return '';

  const configs = schedule.schedule_config.week_of_day_configs || [];
  const enabledConfigs = configs.filter((config) => config.enabled);

  if (enabledConfigs.length === 0) return '';

  // Check if all days have the same hours
  const firstDayIntervals = enabledConfigs[0].intervals;
  const allSameIntervals = enabledConfigs.every(
    (config) =>
      config.intervals.length === firstDayIntervals.length &&
      config.intervals.every(
        (interval, i) =>
          interval.start_time === firstDayIntervals[i].start_time &&
          interval.end_time === firstDayIntervals[i].end_time
      )
  );

  if (allSameIntervals && firstDayIntervals.length > 0) {
    // Format times as 12-hour clock
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    return firstDayIntervals
      .map(
        (interval) =>
          `${formatTime(interval.start_time)} - ${formatTime(
            interval.end_time
          )}`
      )
      .join(', ');
  }

  return 'Varies by day';
};

export default function EventTypeEditor() {
  const { eventTypeId } = useParams<{ eventTypeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(
    eventTypeId !== 'new' && !state?.duplicate
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedScheduleData, setSelectedScheduleData] =
    useState<Schedule | null>(null);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleName, setScheduleName] = useState('Working Hours');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Form state
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventUrl, setEventUrl] = useState('');
  const [eventCategory, setEventCategory] =
    useState<EventTypeCategory>('ONE_ON_ONE');
  const [eventDuration, setEventDuration] = useState('30');
  const [selectedHosts, setSelectedHosts] = useState<string[]>([]);
  const [hostDetails, setHostDetails] = useState<OrganisationMemberResponse[]>(
    []
  );
  const [isActive, setIsActive] = useState(true);
  const [slug, setSlug] = useState('');
  const [automaticallyConfirmBookings, setAutomaticallyConfirmBookings] =
    useState(false);
  const [totalSlots, setTotalSlots] = useState<number>(10);
  const [displayRemainingSlots, setDisplayRemainingSlots] = useState(false);

  // Additional options
  const [minBookingNoticeHours, setMinBookingNoticeHours] = useState(0);
  const [minBookingNoticeMinutes, setMinBookingNoticeMinutes] = useState(0);
  const [minBookingNoticeDays, setMinBookingNoticeDays] = useState(0);
  const [maxBookingNoticeHours, setMaxBookingNoticeHours] = useState<
    number | null
  >(null);
  const [maxBookingNoticeMinutes, setMaxBookingNoticeMinutes] = useState<
    number | null
  >(null);
  const [maxBookingNoticeDays, setMaxBookingNoticeDays] = useState<
    number | null
  >(null);
  const [minTimeBetweenBookingsHours, setMinTimeBetweenBookingsHours] =
    useState(0);
  const [minTimeBetweenBookingsMinutes, setMinTimeBetweenBookingsMinutes] =
    useState(0);
  const [bufferBeforeMinutes, setBufferBeforeMinutes] = useState(0);
  const [bufferAfterMinutes, setBufferAfterMinutes] = useState(0);
  const [maxBookingsPerDay, setMaxBookingsPerDay] = useState<number | null>(
    null
  );
  const [scheduleId, setScheduleId] = useState<string | null>(null);
  const [isScheduleEditorOpen, setIsScheduleEditorOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isCreatingNewSchedule, setIsCreatingNewSchedule] = useState(false);

  // Add new state for schedule editor form
  const [scheduleEditorForm, setScheduleEditorForm] = useState({
    name: 'Working Hours',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    config: DEFAULT_SCHEDULE,
    availability: DEFAULT_AVAILABILITY,
  });

  const [scheduleConfig, setScheduleConfig] =
    useState<Record<DayOfWeek, DayInterval[]>>(DEFAULT_SCHEDULE);
  const [availability, setAvailability] =
    useState<Record<DayOfWeek, boolean>>(DEFAULT_AVAILABILITY);

  const isNewEventType = eventTypeId === 'new';
  const isDuplicatingEventType =
    state?.duplicate === true && state?.eventTypeData;
  const pageTitle = isNewEventType
    ? isDuplicatingEventType
      ? `Duplicate: ${state?.eventTypeData?.name}`
      : 'New Appointment Type'
    : 'Edit Appointment Type';

  // Handle duplicate event data
  useEffect(() => {
    if (isDuplicatingEventType && state?.eventTypeData) {
      const eventType = state.eventTypeData;
      setEventUrl(
        eventType.event_url ||
          `${window.location.origin}/cal.app/${eventType.slug}`
      );
      setEventName(eventType.name);
      setEventDescription(eventType.description || '');
      setEventCategory(eventType.category ?? 'ONE_ON_ONE');
      setEventDuration(eventType.duration.toString());
      setSelectedHosts(eventType.hosts || []);
      setIsActive(eventType.is_active ?? true);
      setAutomaticallyConfirmBookings(
        eventType.automatically_confirm_bookings ?? false
      );
      // Generate a new slug for duplicated event
      setSlug(eventType.name.toLowerCase().replace(/\s+/g, '-') + '-copy');

      // Set additional options using correct field names
      setMinBookingNoticeHours(eventType.min_booking_notice_hours ?? 0);
      setMinBookingNoticeMinutes(eventType.min_booking_notice_minutes ?? 0);
      setMinBookingNoticeDays(eventType.min_booking_notice_days ?? 0);
      setMaxBookingNoticeHours(eventType.max_booking_notice_hours ?? null);
      setMaxBookingNoticeMinutes(eventType.max_booking_notice_minutes ?? null);
      setMaxBookingNoticeDays(eventType.max_booking_notice_days ?? null);
      setMinTimeBetweenBookingsHours(
        eventType.min_time_between_bookings_hours ?? 0
      );
      setMinTimeBetweenBookingsMinutes(
        eventType.min_time_between_bookings_minutes ?? 0
      );
      setBufferBeforeMinutes(eventType.buffer_before_minutes ?? 0);
      setBufferAfterMinutes(eventType.buffer_after_minutes ?? 0);
      setMaxBookingsPerDay(eventType.max_bookings_per_day ?? null);
      setScheduleId(eventType.schedule_id ?? null);

      // Set group-specific fields
      if (eventType.category === 'GROUP') {
        setTotalSlots(eventType.total_slots ?? 10);
        setDisplayRemainingSlots(
          eventType.display_remaining_slots_on_booking_page ?? false
        );
      }
    }
  }, [state]);

  // Load event type data if editing existing one
  useEffect(() => {
    if (!isNewEventType && !isDuplicatingEventType) {
      fetchEventType();
    }
    fetchSchedules();
  }, [eventTypeId]);

  // Add effect to fetch schedule details when scheduleId changes
  useEffect(() => {
    if (scheduleId) {
      fetchScheduleDetails(scheduleId);
    }
  }, [scheduleId]);

  const fetchEventType = async () => {
    if (!eventTypeId || isNewEventType || isDuplicatingEventType) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getEventTypeById({
        path: { event_type_id: eventTypeId },
      });

      if (response.data) {
        const eventType = response.data;
        setEventName(eventType.name);
        setEventDescription(eventType.description || '');
        setEventCategory(eventType.category ?? 'ONE_ON_ONE');
        setEventDuration(eventType.duration.toString());
        setSelectedHosts(eventType.hosts || []);
        setAutomaticallyConfirmBookings(
          eventType.automatically_confirm_bookings ?? false
        );

        // Set group-specific fields
        if (eventType.category === 'GROUP') {
          setTotalSlots(eventType.total_slots ?? 10);
          setDisplayRemainingSlots(
            eventType.display_remaining_slots_on_booking_page ?? false
          );
        }

        // Store host details if available for better display
        if (
          eventType.event_type_hosts &&
          eventType.event_type_hosts.length > 0
        ) {
          console.log('Host details found:', eventType.event_type_hosts);
          setHostDetails(eventType.event_type_hosts);
        }

        setIsActive(eventType.is_active ?? true);
        setSlug(eventType.slug);

        // Handle optional event_url with proper type checking
        const eventUrl = eventType.event_url;
        if (eventUrl && typeof eventUrl === 'string') {
          setEventUrl(eventUrl);
        } else {
          setEventUrl(`${window.location.origin}/cal.app/${eventType.slug}`);
        }

        // Set additional options with correct field names
        setMinBookingNoticeHours(eventType.min_booking_notice_hours ?? 0);
        setMinBookingNoticeMinutes(eventType.min_booking_notice_minutes ?? 0);
        setMinBookingNoticeDays(eventType.min_booking_notice_days ?? 0);
        setMaxBookingNoticeHours(eventType.max_booking_notice_hours ?? null);
        setMaxBookingNoticeMinutes(
          eventType.max_booking_notice_minutes ?? null
        );
        setMaxBookingNoticeDays(eventType.max_booking_notice_days ?? null);
        setMinTimeBetweenBookingsHours(
          eventType.min_time_between_bookings_hours ?? 0
        );
        setMinTimeBetweenBookingsMinutes(
          eventType.min_time_between_bookings_minutes ?? 0
        );
        setBufferBeforeMinutes(eventType.buffer_before_minutes ?? 0);
        setBufferAfterMinutes(eventType.buffer_after_minutes ?? 0);
        setMaxBookingsPerDay(eventType.max_bookings_per_day ?? null);
        setScheduleId(eventType.schedule_id ?? null);
      }
    } catch (error) {
      console.error('Failed to fetch event type:', error);
      setError('Failed to load event type. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to load event type details',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSchedules = async () => {
    console.log('fetchSchedules');
    setIsLoadingSchedules(true);
    try {
      const response = await getSchedules();
      console.log('response', response);
      if (response.data) {
        console.log('response.data', response.data);
        setSchedules(response.data.schedules);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      toast({
        title: 'Error',
        description: 'Failed to load schedules',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  // Handle event hosting type selection (effect on hosts)
  useEffect(() => {
    if (
      (eventCategory === 'ONE_ON_ONE' || eventCategory === 'GROUP') &&
      selectedHosts.length > 1
    ) {
      setSelectedHosts(selectedHosts.length ? [selectedHosts[0]] : []);
    }
  }, [eventCategory]);

  // Automatic slug generation on name change for new event types
  useEffect(() => {
    if (isNewEventType && !isDuplicatingEventType && eventName && !slug) {
      setSlug(eventName.toLowerCase().replace(/\s+/g, '-'));
    }
  }, [eventName, isNewEventType, isDuplicatingEventType, slug]);

  // Update schedule selection handler
  const handleScheduleChange = (value: string) => {
    console.log('Schedule selected:', value);
    setScheduleId(value || null);
    if (value) {
      console.log('Fetching schedule details for:', value);
      fetchScheduleDetails(value);
    } else {
      setSelectedScheduleData(null);
    }
  };

  // Add function to fetch schedule details
  const fetchScheduleDetails = async (scheduleId: string) => {
    console.log('fetchScheduleDetails called with:', scheduleId);
    try {
      const response = await getSchedule({
        path: { schedule_id: scheduleId },
      });
      console.log('Schedule details response:', response);
      if (response.data) {
        setSelectedScheduleData(response.data);

        // Initialize schedule config and availability with defaults first
        const newScheduleConfig = { ...DEFAULT_SCHEDULE };
        const newAvailability = { ...DEFAULT_AVAILABILITY };

        // Then update with actual schedule data
        response.data.schedule_config.week_of_day_configs.forEach((config) => {
          newScheduleConfig[config.day_of_week] = config.intervals;
          newAvailability[config.day_of_week] = config.enabled;
        });

        setScheduleConfig(newScheduleConfig);
        setAvailability(newAvailability);

        // Also update the editor form when editing
        setScheduleEditorForm({
          name: response.data.name,
          timezone: response.data.time_zone,
          config: newScheduleConfig,
          availability: newAvailability,
        });
      }
    } catch (error) {
      console.error('Failed to fetch schedule details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load schedule details',
        variant: 'destructive',
      });
    }
  };

  // Add schedule update handler
  const handleUpdateSchedule = async () => {
    if (!selectedScheduleData) return;

    try {
      const scheduleConfigData = {
        week_of_day_configs: Object.entries(
          scheduleEditorForm.availability
        ).map(([day, enabled]) => ({
          day_of_week: day as DayOfWeek,
          intervals: enabled ? scheduleEditorForm.config[day as DayOfWeek] : [],
          enabled,
        })),
      };

      await updateSchedule({
        path: { schedule_id: selectedScheduleData.id },
        body: {
          name: scheduleEditorForm.name,
          time_zone: scheduleEditorForm.timezone,
          schedule_config: scheduleConfigData,
          default_schedule: selectedScheduleData.default_schedule,
        },
      });

      toast({
        title: 'Success',
        description: 'Schedule updated successfully',
      });

      // Refresh schedule data
      await fetchScheduleDetails(selectedScheduleData.id);
      setIsScheduleEditorOpen(false);
    } catch (error) {
      console.error('Failed to update schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to update schedule',
        variant: 'destructive',
      });
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const scheduleConfigData = {
        week_of_day_configs: Object.entries(
          scheduleEditorForm.availability
        ).map(([day, enabled]) => ({
          day_of_week: day as DayOfWeek,
          intervals: enabled ? scheduleEditorForm.config[day as DayOfWeek] : [],
          enabled,
        })),
      };

      await createSchedule({
        body: {
          name: scheduleEditorForm.name,
          time_zone: scheduleEditorForm.timezone,
          schedule_config: scheduleConfigData,
          default_schedule: schedules.length === 0, // Make default if first schedule
        },
      });

      toast({
        title: 'Success',
        description: 'Schedule created successfully',
      });

      // Refresh schedules and close modal
      await fetchSchedules();
      setIsScheduleEditorOpen(false);

      // Reset form
      setScheduleEditorForm({
        name: 'Working Hours',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        config: DEFAULT_SCHEDULE,
        availability: DEFAULT_AVAILABILITY,
      });
    } catch (error) {
      console.error('Failed to create schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to create schedule',
        variant: 'destructive',
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate based on event category
      if (
        (eventCategory === 'COLLECTIVE' || eventCategory === 'ROUND_ROBIN') &&
        selectedHosts.length < 2
      ) {
        toast({
          title: 'Invalid hosts',
          description: `${
            eventCategory === 'COLLECTIVE' ? 'Collective' : 'Round Robin'
          } events require at least 2 hosts`,
          variant: 'destructive',
        });
        setIsSaving(false);
        return;
      }

      // Prepare event type data with correct field names
      const eventTypeData: EventTypeCreate | EventTypeUpdate = {
        name: eventName,
        description: eventDescription || null,
        category: eventCategory,
        duration: parseInt(eventDuration),
        hosts: selectedHosts.length > 0 ? selectedHosts : null,
        is_team_event:
          eventCategory === 'COLLECTIVE' || eventCategory === 'ROUND_ROBIN',
        is_active: isActive,
        slug,
        automatically_confirm_bookings: automaticallyConfirmBookings,
        // Additional options with correct field names
        min_booking_notice_hours: minBookingNoticeHours,
        min_booking_notice_minutes: minBookingNoticeMinutes,
        min_booking_notice_days: minBookingNoticeDays,
        max_booking_notice_hours: maxBookingNoticeHours,
        max_booking_notice_minutes: maxBookingNoticeMinutes,
        max_booking_notice_days: maxBookingNoticeDays,
        min_time_between_bookings_hours: minTimeBetweenBookingsHours,
        min_time_between_bookings_minutes: minTimeBetweenBookingsMinutes,
        buffer_before_minutes: bufferBeforeMinutes,
        buffer_after_minutes: bufferAfterMinutes,
        max_bookings_per_day: maxBookingsPerDay,
        schedule_id: scheduleId,
      };

      // Add GROUP-specific fields only for GROUP events
      if (eventCategory === 'GROUP') {
        eventTypeData.total_slots = totalSlots;
        eventTypeData.display_remaining_slots_on_booking_page =
          displayRemainingSlots;
      }

      let response;

      if (isNewEventType || isDuplicatingEventType) {
        // Create new event
        response = await createEventType({
          body: eventTypeData as EventTypeCreate,
        });

        if (response.data) {
          toast({
            title: 'Success',
            description: isDuplicatingEventType
              ? 'Event type duplicated successfully'
              : 'Event type created successfully',
          });
          navigate(`/dashboard/event-types`);
        }
      } else if (eventTypeId) {
        // Update existing event
        response = await updateEventType({
          path: { event_type_id: eventTypeId },
          body: eventTypeData as EventTypeUpdate,
        });

        if (response.data) {
          toast({
            title: 'Success',
            description: 'Event type updated successfully',
          });
        }
      }
    } catch (error) {
      console.error('Failed to save event type:', error);
      setError(
        'Failed to save event type. Please check your inputs and try again.'
      );
      toast({
        title: 'Error',
        description: `Failed to ${
          isNewEventType ? 'create' : 'update'
        } event type`,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add a computed property to determine if the event is saved
  const isEventSaved = !isNewEventType && !isDuplicatingEventType;

  const handleCopyUrl = async () => {
    try {
      const urlToCopy = eventUrl || `${window.location.origin}/cal.app/${slug}`;
      await navigator.clipboard.writeText(urlToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      toast({
        title: 'Success',
        description: 'Event URL copied to clipboard',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to copy URL',
        variant: 'destructive',
      });
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Appointment Types', href: '/dashboard/event-types' },
        {
          label: isNewEventType
            ? isDuplicatingEventType
              ? 'Duplicate Appointment Type'
              : 'New Appointment Type'
            : eventName || 'Edit Appointment Type',
        },
      ]}
    >
      <div className="flex flex-col gap-6 h-[calc(100vh-120px)]">
        {/* Fixed Header */}
        <div className="flex items-center justify-between bg-background sticky top-0 z-10 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard/event-types')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/event-types')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!eventName || isSaving || isLoading}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Appointment Type'
              )}
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">
                Loading appointment type...
              </p>
            </div>
          ) : error ? (
            <Alert variant="destructive" className="my-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Main content - 2/3 width */}
              <div className="md:col-span-2 space-y-6">
                {/* Details and Team Members in same row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <EventTypeSection
                    title="Basic Details"
                    description="Name and describe your appointment type"
                    icon={<Info className="h-5 w-5" />}
                  >
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Appointment Name</Label>
                        <Input
                          id="name"
                          placeholder="e.g., 30 Minute Consultation"
                          value={eventName}
                          onChange={(e) => setEventName(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          placeholder="Brief description of the appointment"
                          value={eventDescription}
                          onChange={(e) => setEventDescription(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="url">Appointment URL</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Share this URL with your patients
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 flex items-center">
                            <Input
                              id="url"
                              value={
                                eventUrl ||
                                `${window.location.origin}/cal.app/${slug}`
                              }
                              className="font-mono text-sm"
                              disabled
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleCopyUrl}
                            className="shrink-0"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </EventTypeSection>

                  <EventTypeSection
                    title="Meeting Setup"
                    description="Configure who attends this appointment"
                    icon={<Users className="h-5 w-5" />}
                  >
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="category">Appointment Type</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  Choose how this appointment will be organized
                                  with hosts and attendees
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Select
                          value={eventCategory}
                          onValueChange={(value: string) =>
                            setEventCategory(value as EventTypeCategory)
                          }
                        >
                          <SelectTrigger id="category">
                            <SelectValue placeholder="Select appointment type" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(EVENT_TYPE_DESCRIPTIONS).map(
                              ([type, description]) => (
                                <SelectItem key={type} value={type}>
                                  <div className="flex flex-col">
                                    <span>
                                      {type
                                        .replace('_', ' ')
                                        .replace(/_/g, '-')}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {description}
                                    </span>
                                  </div>
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <EventTypeHostsSelector
                          selectedHosts={selectedHosts}
                          eventCategory={eventCategory}
                          onSelectedHostsChange={setSelectedHosts}
                          initialHostDetails={hostDetails}
                        />
                      </div>
                    </div>
                  </EventTypeSection>
                </div>

                {/* Schedule Section */}
                <EventTypeSection
                  title="Schedule"
                  description="Configure timing and availability for this event"
                  icon={<CalendarRange className="h-5 w-5" />}
                >
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="duration">Duration</Label>
                      </div>
                      <EventTypeDuration
                        duration={eventDuration}
                        onDurationChange={setEventDuration}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="scheduleId">
                          Availability Schedule
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Select
                            value={scheduleId || ''}
                            onValueChange={handleScheduleChange}
                            disabled={isLoadingSchedules}
                          >
                            <SelectTrigger id="scheduleId">
                              <SelectValue
                                placeholder={
                                  isLoadingSchedules
                                    ? 'Loading schedules...'
                                    : 'Select schedule'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {schedules.map((schedule) => (
                                <SelectItem
                                  key={schedule.id}
                                  value={schedule.id}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{schedule.name}</span>
                                    {schedule.default_schedule && (
                                      <Badge
                                        variant="secondary"
                                        className="ml-2"
                                      >
                                        Default
                                      </Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setIsCreatingNewSchedule(true);
                            setScheduleEditorForm({
                              name: 'Working Hours',
                              timezone:
                                Intl.DateTimeFormat().resolvedOptions()
                                  .timeZone,
                              config: DEFAULT_SCHEDULE,
                              availability: DEFAULT_AVAILABILITY,
                            });
                            setIsScheduleEditorOpen(true);
                          }}
                          disabled={isLoadingSchedules}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {selectedScheduleData && (
                        <div className="mt-4 space-y-2 border rounded-lg p-4 bg-card">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">
                                {selectedScheduleData.name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {selectedScheduleData.time_zone}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsCreatingNewSchedule(false);
                                setIsScheduleEditorOpen(true);
                              }}
                            >
                              Edit Schedule
                            </Button>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-start">
                              <Calendar className="mr-2 h-4 w-4 text-muted-foreground mt-0.5" />
                              <div>
                                <div className="text-sm font-medium">
                                  {formatDaysDisplay(selectedScheduleData)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {formatTimeDisplay(selectedScheduleData)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground mt-1">
                        {!scheduleId ? 'Using default schedule' : ''}
                      </p>
                    </div>
                  </div>
                </EventTypeSection>

                {/* Group Event Settings Section - Only shown for GROUP event types */}
                {eventCategory === 'GROUP' && (
                  <EventTypeSection
                    title="Group Event Settings"
                    description="Configure capacity and display options for group events"
                    icon={<Users className="h-5 w-5" />}
                  >
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="totalSlots">Total Slots</Label>
                        <Input
                          id="totalSlots"
                          type="number"
                          min="2"
                          value={totalSlots}
                          onChange={(e) =>
                            setTotalSlots(parseInt(e.target.value) || 2)
                          }
                        />
                        <p className="text-sm text-muted-foreground">
                          Maximum number of attendees for this group event
                        </p>
                      </div>

                      <div className="flex items-center justify-between border p-4 rounded-md">
                        <div className="space-y-0.5">
                          <Label
                            htmlFor="displayRemainingSlots"
                            className="cursor-pointer"
                          >
                            Display Remaining Slots
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Show attendees how many spots are left for this
                            group event
                          </p>
                        </div>
                        <Switch
                          id="displayRemainingSlots"
                          checked={displayRemainingSlots}
                          onCheckedChange={setDisplayRemainingSlots}
                        />
                      </div>
                    </div>
                  </EventTypeSection>
                )}
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="space-y-6">
                <EventTypeSection
                  title="Status"
                  icon={<Bell className="h-5 w-5" />}
                >
                  <EventTypeStatus
                    isActive={isActive}
                    onActiveChange={setIsActive}
                  />

                  <div className="mt-4 space-y-1">
                    <div className="flex justify-between items-center">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">
                            Automatic Confirmation
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Events will be confirmed without requiring host
                          approval
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="auto-confirm"
                          checked={automaticallyConfirmBookings}
                          onCheckedChange={setAutomaticallyConfirmBookings}
                        />
                        <Label htmlFor="auto-confirm">
                          {automaticallyConfirmBookings ? 'On' : 'Off'}
                        </Label>
                      </div>
                    </div>
                  </div>
                </EventTypeSection>

                {/* Buffer Times */}
                <EventTypeSection
                  title="Buffer Times"
                  description="Add buffer time before or after your events"
                  icon={<Timer className="h-5 w-5" />}
                >
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bufferBeforeMinutes">
                        Buffer Before (minutes)
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative">
                              <Input
                                id="bufferBeforeMinutes"
                                type="number"
                                min="0"
                                value={bufferBeforeMinutes}
                                onChange={(e) =>
                                  setBufferBeforeMinutes(
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="pr-8"
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              Time reserved before an event begins
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bufferAfterMinutes">
                        Buffer After (minutes)
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative">
                              <Input
                                id="bufferAfterMinutes"
                                type="number"
                                min="0"
                                value={bufferAfterMinutes}
                                onChange={(e) =>
                                  setBufferAfterMinutes(
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="pr-8"
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              Time reserved after an event ends
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </EventTypeSection>

                {/* Booking Limits */}
                <EventTypeSection
                  title="Booking Limits"
                  description="Set limits on how and when people can book this event"
                  icon={<Hourglass className="h-5 w-5" />}
                >
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Minimum Notice</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label
                            htmlFor="minBookingNoticeDays"
                            className="text-xs text-muted-foreground"
                          >
                            Days
                          </Label>
                          <Input
                            id="minBookingNoticeDays"
                            type="number"
                            min="0"
                            value={minBookingNoticeDays}
                            onChange={(e) =>
                              setMinBookingNoticeDays(
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="minBookingNoticeHours"
                            className="text-xs text-muted-foreground"
                          >
                            Hours
                          </Label>
                          <Input
                            id="minBookingNoticeHours"
                            type="number"
                            min="0"
                            max="23"
                            value={minBookingNoticeHours}
                            onChange={(e) =>
                              setMinBookingNoticeHours(
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="minBookingNoticeMinutes"
                            className="text-xs text-muted-foreground"
                          >
                            Minutes
                          </Label>
                          <Input
                            id="minBookingNoticeMinutes"
                            type="number"
                            min="0"
                            max="59"
                            value={minBookingNoticeMinutes}
                            onChange={(e) =>
                              setMinBookingNoticeMinutes(
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        How far in advance people must book
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Maximum Notice</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label
                            htmlFor="maxBookingNoticeDays"
                            className="text-xs text-muted-foreground"
                          >
                            Days
                          </Label>
                          <Input
                            id="maxBookingNoticeDays"
                            type="number"
                            min="0"
                            value={maxBookingNoticeDays || ''}
                            onChange={(e) =>
                              setMaxBookingNoticeDays(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="No limit"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="maxBookingNoticeHours"
                            className="text-xs text-muted-foreground"
                          >
                            Hours
                          </Label>
                          <Input
                            id="maxBookingNoticeHours"
                            type="number"
                            min="0"
                            max="23"
                            value={maxBookingNoticeHours || ''}
                            onChange={(e) =>
                              setMaxBookingNoticeHours(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="No limit"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="maxBookingNoticeMinutes"
                            className="text-xs text-muted-foreground"
                          >
                            Minutes
                          </Label>
                          <Input
                            id="maxBookingNoticeMinutes"
                            type="number"
                            min="0"
                            max="59"
                            value={maxBookingNoticeMinutes || ''}
                            onChange={(e) =>
                              setMaxBookingNoticeMinutes(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder="No limit"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        How far in the future people can book (leave empty for
                        no limit)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Minimum Time Between Bookings</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label
                            htmlFor="minTimeBetweenBookingsHours"
                            className="text-xs text-muted-foreground"
                          >
                            Hours
                          </Label>
                          <Input
                            id="minTimeBetweenBookingsHours"
                            type="number"
                            min="0"
                            max="23"
                            value={minTimeBetweenBookingsHours}
                            onChange={(e) =>
                              setMinTimeBetweenBookingsHours(
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="minTimeBetweenBookingsMinutes"
                            className="text-xs text-muted-foreground"
                          >
                            Minutes
                          </Label>
                          <Input
                            id="minTimeBetweenBookingsMinutes"
                            type="number"
                            min="0"
                            max="59"
                            value={minTimeBetweenBookingsMinutes}
                            onChange={(e) =>
                              setMinTimeBetweenBookingsMinutes(
                                parseInt(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Required time between consecutive bookings
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="maxBookingsPerDay">
                        Maximum Bookings Per Day
                      </Label>
                      <Input
                        id="maxBookingsPerDay"
                        type="number"
                        min="0"
                        value={maxBookingsPerDay || ''}
                        onChange={(e) =>
                          setMaxBookingsPerDay(
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        placeholder="No limit"
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum number of bookings allowed per day (leave empty
                        for no limit)
                      </p>
                    </div>
                  </div>
                </EventTypeSection>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Editor Modal */}
      <Dialog
        open={isScheduleEditorOpen}
        onOpenChange={(open) => {
          setIsScheduleEditorOpen(open);
          if (!open) {
            setIsCreatingNewSchedule(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isCreatingNewSchedule ? 'Create New Schedule' : 'Edit Schedule'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name</label>
              <input
                type="text"
                value={scheduleEditorForm.name}
                onChange={(e) =>
                  setScheduleEditorForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md"
                placeholder="e.g., Working Hours"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Timezone</label>
              <TimezoneSelect
                value={scheduleEditorForm.timezone}
                onValueChange={(value) =>
                  setScheduleEditorForm((prev) => ({
                    ...prev,
                    timezone: value,
                  }))
                }
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Schedule</label>
              <ScheduleEditor
                schedule={scheduleEditorForm.config}
                availability={scheduleEditorForm.availability}
                onScheduleChange={(config) =>
                  setScheduleEditorForm((prev) => ({ ...prev, config }))
                }
                onAvailabilityChange={(availability) =>
                  setScheduleEditorForm((prev) => ({ ...prev, availability }))
                }
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsScheduleEditorOpen(false);
                  setIsCreatingNewSchedule(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={
                  isCreatingNewSchedule
                    ? handleCreateSchedule
                    : handleUpdateSchedule
                }
              >
                {isCreatingNewSchedule ? 'Create Schedule' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
